/* eslint-disable camelcase */

import React, { useCallback, useMemo, useState } from "react";

// region Imports - External libraries
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@material-ui/core";
import { LineString } from "geojson";
// endregion Imports - External libraries
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import { GlobalMessages, LocationsModuleMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Shared
import { Location } from "@shared/interfaces/location.interface";
import { FALLBACK_CONSTRUCTION_RADIUS_METERS } from "@shared/constants/construction.constants";
import { computeCentroid } from "@shared/utils/geometry";
import { latLngsToLineString, lineStringToLatLngs } from "@shared/utils/geometry/utils";
// endregion Imports - Shared
// region Imports - Services
import api from "@services/api";
// endregion Imports - Services
// region Imports - Hooks
import { useToast } from "@hooks/useToast";
// endregion Imports - Hooks
// region Imports - Components
import MapUserDrawing, { Area } from "@components/Map/MapUserDrawing";
// endregion Imports - Components
// region Imports - Styles
import { Container } from "./styles";
// endregion Imports - Styles

// region Types

/** Result of PATCH locations/adjust-construction/:id_location */
interface AdjustConstructionResult {
  adjusted: boolean;
  auto_justified_alerts: number;
}

type ModalAdjustConstructionLocationProps = {
  open: boolean;
  onClose: () => void;
  location: Location;
  eventLatitude: number;
  eventLongitude: number;
  onAdjusted: (autoJustifiedAlerts: number, currentAlertCovered: boolean) => void;
};
// endregion Types

const EARTH_RADIUS_METERS = 6371008.8;
const DISCHARGE_MARKER_COLOR = "#E53935";
const MAP_HEIGHT = 340;

/** Haversine distance in meters between two coordinates (mirrors the backend ST_DWithin auto-justify check)
 * @param from Origin coordinate
 * @param to Destination coordinate
 */
const distanceInMeters = (from: google.maps.LatLngLiteral, to: google.maps.LatLngLiteral): number => {

  const toRadians = (value: number) => (value * Math.PI) / 180;

  const sinLat = Math.sin(toRadians(to.lat - from.lat) / 2);
  const sinLng = Math.sin(toRadians(to.lng - from.lng) / 2);
  const a = sinLat * sinLat + Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) * sinLng * sinLng;

  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(a));
};

/** Ray-casting point-in-polygon (mirrors the backend checkPointInPolygon used by the auto-justify check),
 * so the "current alert covered" hint does not depend on the optional Google Maps geometry library.
 * @param point Point to test
 * @param polygon Polygon vertices
 */
const isPointInPolygon = (point: google.maps.LatLngLiteral, polygon: google.maps.LatLngLiteral[]): boolean => {

  let inside = false;
  let j = polygon.length - 1;

  for (let i = 0; i < polygon.length; i += 1) {
    const xi = polygon[i].lng;
    const yi = polygon[i].lat;
    const xj = polygon[j].lng;
    const yj = polygon[j].lat;

    const intersect = ((yi > point.lat) !== (yj > point.lat)) && (point.lng < ((xj - xi) * (point.lat - yi)) / (yj - yi) + xi);

    if (intersect) inside = !inside;

    j = i;
  }

  return inside;
};

/**
 * Adjust (and lock) a construction location from an improper discharge alert.
 * Reuses the standard location-drawing map (search + draggable/resizable circle or polygon), pre-drawn
 * with the construction's current area, and shows the improper-discharge point as a reference marker.
 * Saving calls PATCH locations/adjust-construction/:id_location, which auto-justifies the pending
 * improper discharge alerts of travels heading to this construction inside the new area.
 */
const ModalAdjustConstructionLocation: React.FC<ModalAdjustConstructionLocationProps> = ({
  open, onClose, location, eventLatitude, eventLongitude, onAdjusted
}) => {

  // region Hooks
  const { addToast } = useToast();
  const { t } = useTranslation();
  // endregion Hooks
  // region States
  // the area currently drawn on the map (circle or polygon); starts from the construction's own area
  const [drawnArea, setDrawnArea] = useState<Area | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  // endregion States
  // region Constants

  // Where the construction is registered today — pre-draw it so the user adjusts from the current shape
  const initialArea = useMemo<Area | undefined>(() => {

    if (location.polygon_area) return { type: "polygon", location: lineStringToLatLngs(location.polygon_area) };

    const lat = Number(location.latitude);
    const lng = Number(location.longitude);

    if (!location.latitude || !location.longitude || Number.isNaN(lat) || Number.isNaN(lng)) return undefined;

    return {
      type: "circle",
      location: { lat, lng },
      radius: Number(location.radius) > 0 ? Number(location.radius) : FALLBACK_CONSTRUCTION_RADIUS_METERS
    };
  }, [location.polygon_area, location.latitude, location.longitude, location.radius]);

  // The improper-discharge point that originated the adjustment, shown for reference only
  const referenceMarkers = useMemo(() => [{
    location: { lat: eventLatitude, lng: eventLongitude },
    title: t(LocationsModuleMessages.adjustConstructionDischargePoint),
    color: DISCHARGE_MARKER_COLOR,
    blink: true
  }], [eventLatitude, eventLongitude, t]);
  // endregion Constants
  // region Functions

  /** Build the PATCH payload and the "current alert covered" flag from the drawn (or initial) area.
   * Returns null when there is no valid area to save yet.
   */
  const buildAdjustment = useCallback((): {
    payload: { latitude: string, longitude: string, radius?: number, polygon_area?: LineString },
    currentAlertCovered: boolean
  } | null => {

    const area = drawnArea ?? initialArea;

    if (!area) return null;

    const eventPoint = { lat: eventLatitude, lng: eventLongitude };

    if (area.type === "polygon") {

      if (area.location.length < 3) return null;

      // A polygon-shaped construction pins at its centroid (same rule as the Locais screen / worker)
      const centroid = computeCentroid(area.location);

      return {
        payload: {
          latitude: centroid.lat().toString(),
          longitude: centroid.lng().toString(),
          polygon_area: latLngsToLineString(area.location)
        },
        currentAlertCovered: isPointInPolygon(eventPoint, area.location)
      };
    }

    const radius = area.radius > 0 ? area.radius : FALLBACK_CONSTRUCTION_RADIUS_METERS;

    return {
      payload: { latitude: area.location.lat.toString(), longitude: area.location.lng.toString(), radius: area.radius },
      // Whether the alert that originated the adjustment falls inside the new radius (and was auto-justified)
      currentAlertCovered: distanceInMeters(area.location, eventPoint) <= radius
    };
  }, [drawnArea, initialArea, eventLatitude, eventLongitude]);

  /** Save the adjustment: PATCH the construction with the drawn circle or polygon */
  const handleSave = useCallback(async () => {

    const adjustment = buildAdjustment();

    if (!adjustment) {
      addToast({ type: "info", title: t(GlobalMessages.alert), description: t(LocationsModuleMessages.adjustConstructionDrawFirst) });

      return;
    }

    setSaving(true);

    try {

      const { data } = await api.patch(`locations/adjust-construction/${location.id_location}`, adjustment.payload);

      if (data.status === "success") {

        const result = data.result as AdjustConstructionResult;

        addToast({
          type: "success",
          title: t(GlobalMessages.success),
          description: `${t(LocationsModuleMessages.adjustConstructionSuccessPrefix)} ${result.auto_justified_alerts} `
            + `${t(LocationsModuleMessages.adjustConstructionAlertsJustified)}`
        });

        onAdjusted(result.auto_justified_alerts, adjustment.currentAlertCovered);

      } else addToast({ type: "info", title: t(GlobalMessages.alert), description: data.message });

    } catch (error) {

      if (!error.response) addToast({ type: "error", title: t(GlobalMessages.error), description: t(GlobalMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });

    } finally { setSaving(false); }

  }, [addToast, buildAdjustment, location.id_location, onAdjusted, t]);
  // endregion Functions

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth style={{ zIndex: 2100 }}>
      <DialogTitle>{t(LocationsModuleMessages.adjustConstruction)}</DialogTitle>
      <DialogContent dividers>
        <Container>
          {location.name && (
            <div className="locationName">
              {location.name}{location.external_id ? ` (${location.external_id})` : ""}
            </div>
          )}

          <div className="hint">{t(LocationsModuleMessages.adjustConstructionRadiusHint)}</div>

          <div className="drawing">
            <MapUserDrawing
              mapHeight={MAP_HEIGHT}
              type="update"
              initialArea={initialArea}
              referenceMarkers={referenceMarkers}
              returnLocation={(addressData, area) => setDrawnArea(area)}
            />
          </div>
        </Container>
      </DialogContent>
      <DialogActions>
        <Button disableRipple onClick={onClose} color="primary" disabled={saving}>
          {t(GlobalMessages.close)}
        </Button>
        <Button disableRipple onClick={handleSave} color="primary" variant="contained" disabled={saving}>
          {t(LocationsModuleMessages.adjustConstructionSave)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalAdjustConstructionLocation;
